import "../SectionScoreCard.css";

import { FC, useId } from "react";

import {
  AIActionSuggestion,
  AIActionSuggestionRow,
} from "../../../ai/components/AIActionSuggestion";

import { SectionScoreDisplayFormat } from "components/server-types";
import { Trans, t } from "i18n/i18n";
import { EmptyCard } from "mds/components/EmptyCard";
import { FlagIcon, ScoreIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { NestedOutcomeGroupType, selectCurrentOrg } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { getMaxSectionScoreForOrg, getRubricScoreFromPercent } from "utils/assessment";

export type ProgressOverviewBoxProps = {
  outcomeGroups: NestedOutcomeGroupType[];
  totalScore: number;
  totalAssessed: number;
  totalSectionScore?: number | null;
};

const formatScore = (value: number): string => value.toFixed(1).replace(/\.0$/, "");

const TotalSectionScoreMeter: FC<{ totalSectionScore: number }> = ({ totalSectionScore }) => {
  const org = useObjectSelector(selectCurrentOrg, ["section_score_display_format"]);
  const id = useId();

  if (!org) return null;

  const maxScore = getMaxSectionScoreForOrg(org);
  const isPercent = org.section_score_display_format === SectionScoreDisplayFormat.Percent;

  return (
    <div className="section-score-card pt-3">
      <div className="flex min-w-1 items-center justify-between gap-1">
        <label className="min-w-1 shrink truncate text-sm font-normal" htmlFor={id}>
          {t("progress_overview.total_score")}
        </label>

        <div className="flex shrink-0 items-baseline gap-1 whitespace-nowrap">
          {isPercent ? (
            <span className="text-lg font-semibold text-blue">
              {formatScore(totalSectionScore)}
            </span>
          ) : (
            <ScoreIcon className="text-lg" size="l" value={totalSectionScore} />
          )}

          <span className="text-xs font-normal text-black-tint-40">
            {" "}
            / {maxScore}
            {isPercent ? "%" : undefined}
          </span>
        </div>
      </div>

      <meter className="mt-2 h-1 w-full" id={id} max={maxScore} value={totalSectionScore} />
    </div>
  );
};

export const ProgressOverviewBox: FC<ProgressOverviewBoxProps> = ({
  outcomeGroups,
  totalScore,
  totalAssessed,
  totalSectionScore = null,
}) => {
  const org = useAppSelector(selectCurrentOrg);

  const groupScores = outcomeGroups.map((group) => group.averageOutcomeScore!);
  const maxScore = groupScores.length > 0 ? Math.max(...groupScores) : 0;
  const minScore = groupScores.length > 0 ? Math.min(...groupScores) : 0;

  const strongGroup = outcomeGroups.find((group) => group.averageOutcomeScore === maxScore);
  const weakGroup = outcomeGroups.find((group) => group.averageOutcomeScore === minScore);

  const rubricLevel = org?.rubric?.find((level) => level.score === Math.floor(totalScore));

  if (!org) return null;

  const equivalentRubricScore =
    org.section_score_display_format === SectionScoreDisplayFormat.Percent &&
    totalSectionScore != null
      ? getRubricScoreFromPercent(totalSectionScore, org.rubric)
      : null;
  const equivalentRubricLevel =
    equivalentRubricScore != null
      ? org.rubric.find((level) => level.score === Math.floor(equivalentRubricScore))
      : undefined;
  const showPercentCopy = equivalentRubricScore != null && equivalentRubricLevel != null;

  if (totalAssessed === 0 && totalSectionScore == null) {
    return (
      <EmptyCard
        className="mb-4"
        description={t("progress_overview.no_outcomes_assessed.description")}
        icon={<FlagIcon className="text-blue" />}
        title={t("progress_overview.no_outcomes_assessed.title")}
      />
    );
  }

  if (totalAssessed > 0 && !rubricLevel) return null;

  return (
    <div className="w-full rounded-xl border border-blue-tint-40 bg-blue-tint-97 p-6 text-base text-black-tint-20">
      <h2 className="text-blue">{t("progress_overview.header")}</h2>

      {totalSectionScore != null && (
        <TotalSectionScoreMeter totalSectionScore={totalSectionScore} />
      )}

      {totalAssessed > 0 && rubricLevel && (
        <>
          <div className="body-s pt-3">
            <Trans
              components={{ span: <span className="font-semibold" /> }}
              i18nKey={
                org.section_score_display_format === SectionScoreDisplayFormat.Percent &&
                totalSectionScore != null
                  ? "progress_overview.overall_score_percent"
                  : "progress_overview.overall_score"
              }
              t={t}
              values={{
                score: showPercentCopy ? formatScore(equivalentRubricScore) : totalScore,
                total: org.rubric.length - 1,
                result: showPercentCopy ? equivalentRubricLevel.name : rubricLevel.name,
                percent: totalSectionScore != null ? formatScore(totalSectionScore) : undefined,
              }}
            />
          </div>

          <div className="body-s pt-3">
            {t("progress_overview.summary", {
              totalAssessed,
              groupTotal: outcomeGroups.length,
              strongName: strongGroup?.title,
              strongScore: strongGroup?.averageOutcomeScore,
              weakName: weakGroup?.title,
              weakScore: weakGroup?.averageOutcomeScore,
            })}
          </div>
        </>
      )}

      <div className="mt-6 text-xs text-black-tint-40">{t("progress_overview.actions.title")}</div>

      <AIActionSuggestionRow>
        <AIActionSuggestion
          fragment={t("progress_overview.actions.career.fragment")}
          fullText={t("progress_overview.actions.career.description")}
        />

        <AIActionSuggestion
          fragment={t("progress_overview.actions.study_plan.fragment")}
          fullText={t("progress_overview.actions.study_plan.description")}
        />

        <AIActionSuggestion
          fragment={t("progress_overview.actions.interview.fragment")}
          fullText={t("progress_overview.actions.interview.description")}
        />
      </AIActionSuggestionRow>
    </div>
  );
};
